import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody, Chip, TextField, MenuItem,
  Stack, Button, TablePagination, Tooltip, Alert, CircularProgress, Collapse, IconButton,
} from '@mui/material';
import { Refresh, ExpandMore, ExpandLess, Download } from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore, ROLE_LABELS } from '../../store/auth';

// Super-admin only: every API request made by every account (logins, changes, page views, denials).

const CAT_COLOR: Record<string, 'primary' | 'warning' | 'default'> = { auth: 'primary', change: 'warning', view: 'default' };
const statusColor = (s: number) => s >= 500 ? 'error' : s === 401 || s === 403 ? 'warning' : s >= 400 ? 'default' : 'success';

export const AuditLogPage: React.FC = () => {
  const role = useAuthStore(s => s.user?.role);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [open, setOpen] = useState<number | null>(null);
  const [f, setF] = useState({ username: '', category: 'auth,change', status: '', path: '', ip: '', from: '', to: '' });

  const params = useCallback((limit: number, offset: number) => {
    const p: any = { limit, offset };
    if (f.username) p.username = f.username;
    if (f.category) p.category = f.category;
    if (f.status) p.status = f.status;
    if (f.path) p.path = f.path;
    if (f.ip) p.ip = f.ip;
    if (f.from) p.from = new Date(f.from).toISOString();
    if (f.to) p.to = new Date(f.to).toISOString();
    return p;
  }, [f]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/audit', { params: params(perPage, page * perPage) });
      setRows(r.data.data); setTotal(r.data.total);
    } catch { setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [params, page, perPage]);

  useEffect(() => { if (role === 'super_admin') load(); }, [role, load]);
  useEffect(() => { if (role === 'super_admin') api.get('/audit/users').then(r => setUsers(r.data)).catch(() => {}); }, [role]);

  if (role !== 'super_admin') return <Alert severity="warning">This page is available to super admins only.</Alert>;

  const set = (k: string) => (e: any) => { setPage(0); setF({ ...f, [k]: e.target.value }); };

  const exportCsv = () => {
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const cols = ['at', 'username', 'role', 'ip', 'category', 'method', 'path', 'query', 'status', 'duration_ms'];
    const csv = [cols.join(',')].concat(rows.map(r => cols.map(c => esc(r[c])).join(','))).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">
            Every request by every account: logins, changes and page views, with who, when, from where and the result.
          </Typography>
        </Box>
        <Button startIcon={<Download />} onClick={exportCsv} disabled={!rows.length}>Export page</Button>
        <Button startIcon={<Refresh />} variant="outlined" onClick={load}>Refresh</Button>
      </Stack>

      <Card variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(7, 1fr)' }, gap: 1 }}>
          <TextField select size="small" label="User" value={f.username} onChange={set('username')}>
            <MenuItem value="">All users</MenuItem>
            {users.map(u => <MenuItem key={u.username} value={u.username}>{u.username}{u.role ? ` (${ROLE_LABELS[u.role] || u.role})` : ''}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Activity" value={f.category} onChange={set('category')}>
            <MenuItem value="auth,change">Logins + changes</MenuItem>
            <MenuItem value="auth">Logins / 2FA / password</MenuItem>
            <MenuItem value="change">Changes</MenuItem>
            <MenuItem value="view">Page views / reads</MenuItem>
            <MenuItem value="">Everything</MenuItem>
          </TextField>
          <TextField select size="small" label="Result" value={f.status} onChange={set('status')}>
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="denied">Denied (401/403)</MenuItem>
            <MenuItem value="failed">Failed (4xx/5xx)</MenuItem>
          </TextField>
          <TextField size="small" label="Path contains" value={f.path} onChange={set('path')} />
          <TextField size="small" label="IP" value={f.ip} onChange={set('ip')} />
          <TextField size="small" type="datetime-local" label="From" InputLabelProps={{ shrink: true }} value={f.from} onChange={set('from')} />
          <TextField size="small" type="datetime-local" label="To" InputLabelProps={{ shrink: true }} value={f.to} onChange={set('to')} />
        </Box>
      </Card>

      <Card variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell><TableCell>User</TableCell><TableCell>IP</TableCell>
              <TableCell>Activity</TableCell><TableCell>Request</TableCell><TableCell align="right">Result</TableCell>
              <TableCell align="right">ms</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={22} /></TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No activity for these filters</TableCell></TableRow>}
            {!loading && rows.map(r => (
              <React.Fragment key={r.id}>
                <TableRow hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(r.at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.username || '—'}</Typography>
                    {r.role && <Typography variant="caption" color="text.secondary">{ROLE_LABELS[r.role] || r.role}</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{r.ip || '—'}</Typography></TableCell>
                  <TableCell><Chip size="small" label={r.category} color={CAT_COLOR[r.category] || 'default'} variant={r.category === 'view' ? 'outlined' : 'filled'} /></TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Typography variant="caption" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                      <b>{r.method}</b> {r.path}{r.query ? `?${r.query}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right"><Chip size="small" label={r.status} color={statusColor(r.status) as any} /></TableCell>
                  <TableCell align="right">{r.duration_ms}</TableCell>
                  <TableCell padding="checkbox">
                    {(r.body || r.user_agent) && (
                      <Tooltip title="Details">
                        <IconButton size="small" onClick={() => setOpen(open === r.id ? null : r.id)}>
                          {open === r.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 0, borderBottom: open === r.id ? undefined : 'none' }}>
                    <Collapse in={open === r.id} unmountOnExit>
                      <Box sx={{ py: 1 }}>
                        {r.user_agent && <Typography variant="caption" color="text.secondary" display="block">{r.user_agent}</Typography>}
                        {r.body && <Box component="pre" sx={{ m: 0, mt: 0.5, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(r.body, null, 2)}</Box>}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} rowsPerPage={perPage}
          onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]} />
      </Card>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Passwords, 2FA codes and tokens are never stored. Page views are kept 90 days; logins, changes and denials 1 year.
      </Typography>
    </Box>
  );
};
